import React from 'react';

function InvestmentsRealEstate() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/images/real-estate/real-estate-6846366_640.jpg" alt="Real Estate" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Real Estate</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Commercial properties, residential developments, REITs, and PropTech platforms transforming the built environment.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Real Estate Investment Strategy</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We invest across the real estate value chain, from income-producing properties to technology platforms disrupting traditional real estate.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">The Real Estate Opportunity</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Real estate represents the world's largest asset class at over $280 trillion, offering diversification, income generation, and inflation protection. Urbanization, demographic shifts, and technology disruption are creating unprecedented opportunities across commercial, residential, industrial, and specialty property sectors.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our real estate investment approach combines direct property ownership, REIT investments, private equity funds, and PropTech ventures. We focus on prime locations with strong fundamentals, sponsor quality, and asset-level cash flow visibility. Our underwriting emphasizes sustainable income, value-add potential, and exit optionality.
              </p>
              <div className="bg-indigo-50 border-l-4 border-indigo-600 p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-indigo-700">Market Size:</strong> Global real estate investment market exceeds $280T with commercial real estate representing $32T. PropTech funding reached $32B in 2023 as technology reshapes the sector.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/real-estate/house-1867187_640.jpg" alt="Real estate thesis" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-indigo-600 opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Property Sectors */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Investment Focus</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {t:'Commercial Real Estate', d:'Office buildings, retail centers, mixed-use developments in prime urban locations with strong tenant covenants and long-term lease structures.', img:'/images/real-estate/real-estate-6846366_640.jpg'},
              {t:'Industrial & Logistics', d:'Warehouses, distribution centers, data centers, and last-mile logistics facilities benefiting from e-commerce growth and supply chain evolution.', img:'/images/real-estate/purchase-3113198_640.jpg'},
              {t:'PropTech & Innovation', d:'Real estate technology platforms including property management software, transaction platforms, smart building systems, and virtual property solutions.', img:'/images/real-estate/living-room-1643855_640.jpg'}
            ].map(x => (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <img src={x.img} alt={x.t} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{x.t}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {[
              {title: 'Residential Investments', desc: 'Multifamily properties, single-family rental portfolios, senior housing, student housing, and build-to-rent communities in high-growth markets.'},
              {title: 'Specialty Real Estate', desc: 'Self-storage, healthcare facilities, hospitality assets, and alternative property types with specialized operational expertise and niche market dynamics.'}
            ].map(item => (
              <div key={item.title} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Investment Approach</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Disciplined underwriting combining location analysis, cash flow modeling, and risk assessment.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Investment Criteria</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-indigo-600 pl-5 bg-indigo-50/50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Location & Demographics</h4>
                  <p className="text-sm text-gray-600">Prime locations in growing MSAs with strong population growth, employment fundamentals, and infrastructure development.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-5 bg-gray-50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Cash Flow & Yield</h4>
                  <p className="text-sm text-gray-600">Stabilized assets with 6-8% unlevered yields, or value-add opportunities with clear paths to 12-15%+ IRRs through repositioning or development.</p>
                </div>
                <div className="border-l-4 border-indigo-600 pl-5 bg-indigo-50/50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Sponsor Quality</h4>
                  <p className="text-sm text-gray-600">Experienced operators with local market expertise, strong track records, conservative leverage (typically 60-65% LTV), and aligned incentives.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-5 bg-gray-50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Exit Strategy</h4>
                  <p className="text-sm text-gray-600">Multiple exit paths including sale to institutional buyers, REIT acquisition, portfolio aggregation, or long-term hold for income.</p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <img src="/new-assets/pexels-yankrukov-7792761.jpg" alt="Real estate investment" className="w-full h-64 object-cover rounded-2xl shadow-lg" />
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl">
                <h4 className="font-semibold mb-4">Due Diligence Process</h4>
                <ul className="space-y-2 text-sm text-white/90">
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Property inspection & condition assessment</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Market analysis & comparable sales review</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Financial modeling & sensitivity analysis</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Environmental & title due diligence</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Legal structure & tax optimization review</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REITs & Funds */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Investment Vehicles</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Multiple pathways to real estate exposure tailored to your liquidity needs and return objectives.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {title: 'Public REITs', desc: 'Liquid exposure to diversified property portfolios through publicly-traded equity and mortgage REITs with daily liquidity and 4-6% dividend yields.'},
              {title: 'Private Equity Funds', desc: 'Institutional-quality funds targeting opportunistic and value-add strategies with 5-10 year hold periods and 15-20% net IRR targets.'},
              {title: 'Direct Ownership', desc: 'Co-investment opportunities in individual properties or portfolios providing control, tax benefits, and concentrated exposure to specific assets.'}
            ].map((vehicle, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-3">{vehicle.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{vehicle.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Metrics */}
      <section className="py-16 bg-gradient-to-br from-indigo-900 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Portfolio Performance</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Our real estate investments deliver attractive risk-adjusted returns with downside protection.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {metric: '$2.8B', label: 'Real estate AUM'},
              {metric: '14.2%', label: 'Average net IRR since inception'},
              {metric: '92%', label: 'Portfolio occupancy rate'},
              {metric: '150+', label: 'Properties across portfolio'}
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{item.metric}</div>
                <div className="text-sm text-white/80">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Explore Real Estate Investments</h3>
          <p className="mt-2 text-gray-600">Access institutional-quality real estate opportunities with our experienced investment team.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsRealEstate;

